/**
 * DokumentGenerator v5 — WinValue-Style Professioneller Dokumenten-Editor
 * 
 * Layout nach WinValue-Vorbild:
 * - Kopfzeile: Logo links + grauer Balken rechts (Firmenname, Inhaber, Titel)
 * - Referenzzeile: AZ | Kennz | SN | VN + Datum (auf JEDER Seite)
 * - Absender/Empfänger + Kontaktdaten (nur Seite 1)
 * - Strukturierter Datenblock (Aktenzeichen, FIN, Versicherung etc.)
 * - Fußzeile: Minimalistisch mit Firmendaten (Rechnung) oder nur Seitenzahl
 * - Schrift: Montserrat / Inter
 * 
 * Dual-Engine Print:
 * - WeasyPrint: CSS Paged Media running elements
 * - Chrome: position:fixed + table spacers
 */
import { useState, useRef, useCallback } from 'react';
import {
  FileText, Download, Printer, Eye, Edit3, Save, X, ChevronDown,
  Plus, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import { MIETGO_BRIEFKOPF, LENNEGLAS_BRIEFKOPF, LCS_GMBH_BRIEFKOPF } from '@/lib/lcs-briefkopf';

// ============================================================
// TYPES
// ============================================================
interface BriefkopfData {
  firmenname: string;
  zusatz: string;
  inhaber: string;
  titel: string;
  qualifikation?: string;
  adresse: string;
  plz_ort: string;
  telefon: string;
  mobil: string;
  email: string;
  web: string;
  logo_url: string;
  bankname?: string;
  iban?: string;
  bic?: string;
  steuernummer?: string;
  ust_id?: string;
}

interface EmpfaengerData {
  firma?: string;
  anrede?: string;
  name: string;
  strasse?: string;
  plz_ort?: string;
  land?: string;
}

interface AktenDaten {
  aktenzeichen?: string;
  kennzeichen?: string;
  fin?: string;
  schadennummer?: string;
  versicherung?: string;
  versicherungsnummer?: string;
  schadenort?: string;
  schadentag?: string;
  kennzeichen_gegner?: string;
  auftragsdatum?: string;
  kunde_name?: string;
}

interface DokumentData {
  typ: 'anschreiben' | 'stellungnahme' | 'freitext' | 'rechnung';
  betreff: string;
  anrede: string;
  inhalt: string;
  grussformel: string;
  unterschrift_name: string;
  unterschrift_titel: string;
  unterschrift_zusatz?: string;
  anlagen?: string[];
}

interface DokumentGeneratorProps {
  aktenNr?: string;
  empfaenger?: Partial<EmpfaengerData>;
  betrieb?: string;
  aktenDaten?: Partial<AktenDaten>;
  onClose?: () => void;
  onSave?: (html: string, filename: string) => void;
}

// ============================================================
// BRIEFKOPF PRESETS
// ============================================================
const BRIEFKOPF_PRESETS: Record<string, BriefkopfData> = {
  'gutachter-koop': {
    firmenname: 'Kfz Sachverständigenbüro Koop',
    zusatz: 'SV Büro Koop',
    inhaber: 'Andreas Koop',
    titel: 'Inh. Andreas Koop',
    qualifikation: 'Kfz Techniker Meister & Kfz Sachverständiger',
    adresse: 'Rahmedestraße 65',
    plz_ort: '58507 Lüdenscheid',
    telefon: '02351 926 5740',
    mobil: '0170 500 4022',
    email: 'info@gutachter-koop.de',
    web: 'gutachter-koop.de',
    logo_url: '/logo-dark.png',
    bankname: 'Sparkasse Lüdenscheid',
    iban: 'DE XX XXXX XXXX XXXX XXXX XX',
    bic: 'WELADED1LSD',
    steuernummer: 'XXX/XXXX/XXXX',
  },
  'mietgo': {
    ...MIETGO_BRIEFKOPF,
    telefon: '',
    mobil: '',
  },
  'lcs-gmbh': {
    ...LCS_GMBH_BRIEFKOPF,
    telefon: '',
    mobil: '',
  },
  'lenneglas': {
    ...LENNEGLAS_BRIEFKOPF,
    telefon: '',
    mobil: '',
  },
  'unfallgiganten': {
    firmenname: 'Unfallgiganten',
    zusatz: 'Unfallgiganten',
    inhaber: 'Andreas Koop',
    titel: 'Inh. Andreas Koop',
    qualifikation: 'Geschäftsführer',
    adresse: 'Rahmedestraße 65',
    plz_ort: '58507 Lüdenscheid',
    telefon: '02351 926 5740',
    mobil: '0170 500 4022',
    email: 'info@unfallgiganten.de',
    web: 'unfallgiganten.de',
    logo_url: '/logos/unfallgiganten-logo.webp',
  },
  'protex': {
    firmenname: 'Protex Fahrzeugaufbereitung',
    zusatz: 'Protex',
    inhaber: 'Andreas Koop',
    titel: 'Inh. Andreas Koop',
    qualifikation: 'Geschäftsführer',
    adresse: 'Rahmedestraße 65',
    plz_ort: '58507 Lüdenscheid',
    telefon: '02351 926 5740',
    mobil: '0170 500 4022',
    email: 'info@protex-aufbereitung.de',
    web: 'protex-aufbereitung.de',
    logo_url: '/logo-dark.png',
  },
};

// ============================================================
// VORLAGEN
// ============================================================
const VORLAGEN: Record<string, Partial<DokumentData>> = {
  // ===== MIETWAGEN-VORLAGEN =====
  'mw-rundschreiben-anwaelte': {
    typ: 'anschreiben',
    betreff: 'Wichtige Mitteilung — Mietwagenabrechnung über MietGo / LCS GmbH',
    anrede: 'Sehr geehrte Damen und Herren,',
    inhalt: `hiermit möchten wir Sie darüber informieren, dass die Mietwagenabrechnung für Unfallersatzfahrzeuge, die bisher über das Kfz-Sachverständigenbüro Koop vermittelt wurden, ab sofort unter der Marke MietGo erfolgt.

MietGo ist eine Marke der LCS GmbH, Vertretungsberechtigt: Viktor Malinin. Die LCS GmbH betreibt ab sofort ein eigenes Forderungsmanagement für alle Mietwagenkosten aus Unfallschäden.

Für Sie ändert sich Folgendes:

• Rechnungsstellung erfolgt künftig durch die LCS GmbH / MietGo
• Korrespondenz bitte an: info@mietgo.de
• Postanschrift: LCS GmbH, Rahmedestraße 65, 58509 Lüdenscheid

Bestehende Forderungen werden von uns weiterhin betreut und abgewickelt. Wir bitten Sie, unsere neuen Kontaktdaten in Ihren Unterlagen zu aktualisieren.

Für Rückfragen stehen wir Ihnen jederzeit gerne zur Verfügung.`,
    grussformel: 'Mit freundlichen Grüßen',
  },
  'mw-rundschreiben-versicherungen': {
    typ: 'anschreiben',
    betreff: 'Mitteilung — Änderung der Abrechnungsstelle für Mietwagenkosten',
    anrede: 'Sehr geehrte Damen und Herren,',
    inhalt: `hiermit teilen wir Ihnen mit, dass die Abrechnung von Mietwagenkosten aus Unfallschäden, die bisher über das Kfz-Sachverständigenbüro Koop erfolgten, ab sofort durch die LCS GmbH unter der Marke MietGo abgewickelt werden.

Die LCS GmbH betreibt ein eigenes Forderungsmanagement. Sämtliche bestehenden und zukünftigen Mietwagenforderungen werden von uns direkt bearbeitet.

Bitte aktualisieren Sie Ihre Unterlagen wie folgt:

• Firma: LCS GmbH / MietGo
• Anschrift: Rahmedestraße 65, 58509 Lüdenscheid
• E-Mail: info@mietgo.de

Wir bitten um Bestätigung des Eingangs dieses Schreibens per E-Mail an info@mietgo.de.

Für Rückfragen stehen wir Ihnen jederzeit zur Verfügung.`,
    grussformel: 'Mit freundlichen Grüßen',
  },
  'mw-forderung-versicherung': {
    typ: 'anschreiben',
    betreff: 'Offene Forderung — Mietwagenkosten, Schadennr. {schadennr}',
    anrede: 'Sehr geehrte Damen und Herren,',
    inhalt: `in vorgenannter Angelegenheit wenden wir uns an Sie bezüglich einer offenen Forderung aus Mietwagenkosten.

Der/die Geschädigte hat nach dem Verkehrsunfall ein Unfallersatzfahrzeug über MietGo (LCS GmbH) angemietet. Die Mietwagenkosten wurden ordnungsgemäß in Rechnung gestellt. Bis heute ist jedoch keine Zahlung bei uns eingegangen.

Wir bitten Sie höflich, den offenen Betrag zeitnah zu regulieren oder uns den aktuellen Bearbeitungsstand mitzuteilen.

Bitte antworten Sie per E-Mail an: info@mietgo.de

Sollte die Zahlung bereits veranlasst sein, bitten wir um Übersendung des Zahlungsbelegs.

Für Rückfragen stehen wir Ihnen gerne zur Verfügung.`,
    grussformel: 'Mit freundlichen Grüßen',
  },
  'mw-mahnung-versicherung': {
    typ: 'anschreiben',
    betreff: 'Zahlungserinnerung — Mietwagenkosten, Schadennr. {schadennr}',
    anrede: 'Sehr geehrte Damen und Herren,',
    inhalt: `wir nehmen Bezug auf unser Schreiben vom [DATUM] bezüglich der offenen Mietwagenkosten in vorgenannter Angelegenheit.

Leider ist bis heute keine Zahlung bei uns eingegangen. Wir bitten Sie daher erneut und mit Nachdruck, den offenen Betrag innerhalb von 14 Tagen nach Zugang dieses Schreibens zu überweisen.

Sollte die Zahlung nicht fristgerecht erfolgen, sehen wir uns gezwungen, die Angelegenheit an unseren Rechtsanwalt zur weiteren Durchsetzung zu übergeben.

Bitte antworten Sie per E-Mail an: info@mietgo.de`,
    grussformel: 'Mit freundlichen Grüßen',
  },
  'mw-mietdauer-begruendung': {
    typ: 'anschreiben',
    betreff: 'Begründung der Mietdauer — Schadennr. {schadennr}',
    anrede: 'Sehr geehrte Damen und Herren,',
    inhalt: `zu Ihrer Anfrage bezüglich der Mietdauer des Ersatzfahrzeugs nehmen wir wie folgt Stellung:

[ZUTREFFENDES AUSWÄHLEN UND ERGÄNZEN]

1. Verkehrssicherheit: Das Fahrzeug des Geschädigten war nach dem Unfall nicht mehr verkehrssicher. Eine Weiterfahrt war aus Sicherheitsgründen nicht möglich. Der Geschädigte war daher ab dem Unfalltag auf ein Ersatzfahrzeug angewiesen.

2. Reparaturdauer: Die Reparatur des Fahrzeugs dauerte [X] Werktage. Hinzu kamen [X] Tage für die Ersatzteilbeschaffung. Die Mietdauer entspricht der tatsächlichen Reparaturdauer zuzüglich angemessener Vorlauf- und Nachlaufzeiten.

3. Zerlegung für Begutachtung: Für die sachverständige Begutachtung mussten Verkleidungsteile und/oder Stoßfänger zerlegt werden. Während dieser Zerlegung war das Fahrzeug nicht fahrbereit. Die Zerlegung stellt eine Art Reparatur dar, für deren Dauer der Geschädigte Anspruch auf Mobilität hat.

4. Notreparatur: Eine Notreparatur war nicht möglich/zumutbar, da [BEGRÜNDUNG].

5. Totalschaden: Bei dem Fahrzeug lag ein wirtschaftlicher Totalschaden vor. Die Mietdauer orientiert sich an der angemessenen Wiederbeschaffungsdauer von [X] Tagen gemäß Gutachten.

Die Mietdauer war daher in vollem Umfang erforderlich und angemessen.`,
    grussformel: 'Mit freundlichen Grüßen',
  },
  // ===== GUTACHTEN-VORLAGEN =====
  'anschreiben-anwalt': {
    typ: 'anschreiben',
    betreff: 'Abrechnungsschreiben — Schadennr. {schadennr}',
    anrede: 'Sehr geehrte Damen und Herren,',
    inhalt: `hiermit bitten wir Sie, uns das vollständige Abrechnungsschreiben der gegnerischen Versicherung für den o.g. Schadensfall zukommen zu lassen.

Unser Gutachten liegt Ihnen bereits vor. Wir bitten um Prüfung, ob seitens der Versicherung Kürzungen an den Sachverständigenkosten, der Wertminderung oder den Reparaturkosten vorgenommen wurden.

Sollte dies der Fall sein, bitten wir Sie, die vollständige Durchsetzung unserer Honorarforderung auf Grundlage der geschlossenen Auftrags- und Honorarvereinbarung geltend zu machen. Wir verweisen hierbei auf das Sachverständigenrisiko gemäß BGH-Rechtsprechung.`,
    grussformel: 'Mit freundlichen Grüßen',
  },
  'stellungnahme': {
    typ: 'stellungnahme',
    betreff: 'Stellungnahme zu technischen Einwänden - Gutachten {aktenzeichen}',
    anrede: 'Sehr geehrte Damen und Herren,',
    inhalt: `zu dem uns vorliegenden Prüfbericht nehmen wir wie folgt Stellung:

[Hier Stellungnahme einfügen]

Wir halten an unserer Kalkulation und Bewertung vollumfänglich fest.`,
    grussformel: 'Mit freundlichen Grüßen',
  },
  'freitext': {
    typ: 'freitext',
    betreff: '',
    anrede: 'Sehr geehrte Damen und Herren,',
    inhalt: '',
    grussformel: 'Mit freundlichen Grüßen',
  },
};

// ============================================================
// COMPONENT
// ============================================================
export default function DokumentGenerator({
  aktenNr,
  empfaenger: initialEmpfaenger,
  betrieb = 'gutachter-koop',
  aktenDaten: initialAktenDaten,
  onClose,
  onSave,
}: DokumentGeneratorProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [briefkopf, setBriefkopf] = useState<BriefkopfData>(
    BRIEFKOPF_PRESETS[betrieb] || BRIEFKOPF_PRESETS['gutachter-koop']
  );

  const [empfaenger, setEmpfaenger] = useState<EmpfaengerData>({
    firma: initialEmpfaenger?.firma || '',
    anrede: initialEmpfaenger?.anrede || 'Firma',
    name: initialEmpfaenger?.name || '',
    strasse: initialEmpfaenger?.strasse || '',
    plz_ort: initialEmpfaenger?.plz_ort || '',
    land: initialEmpfaenger?.land || '',
  });

  const [aktenDaten, setAktenDaten] = useState<AktenDaten>({
    aktenzeichen: initialAktenDaten?.aktenzeichen || aktenNr || '',
    kennzeichen: initialAktenDaten?.kennzeichen || '',
    fin: initialAktenDaten?.fin || '',
    schadennummer: initialAktenDaten?.schadennummer || '',
    versicherung: initialAktenDaten?.versicherung || '',
    versicherungsnummer: initialAktenDaten?.versicherungsnummer || '',
    schadenort: initialAktenDaten?.schadenort || '',
    schadentag: initialAktenDaten?.schadentag || '',
    kennzeichen_gegner: initialAktenDaten?.kennzeichen_gegner || '',
    auftragsdatum: initialAktenDaten?.auftragsdatum || '',
  });

  const [dokument, setDokument] = useState<DokumentData>({
    typ: 'anschreiben',
    betreff: '',
    anrede: 'Sehr geehrte Damen und Herren,',
    inhalt: '',
    grussformel: 'Mit freundlichen Grüßen',
    unterschrift_name: briefkopf.inhaber,
    unterschrift_titel: 'Kfz Techniker-Meister, Rennmechaniker',
    unterschrift_zusatz: 'Kfz Sachverständiger - Mitglied im MAS\nBVSK Akademie',
    anlagen: [],
  });

  const [showVorlagen, setShowVorlagen] = useState(false);
  const datum = new Date().toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' });

  // Build reference line: AZ: xxx | KENNZ | SN: xxx | VN: xxx
  const buildRefLine = () => {
    const parts: string[] = [];
    if (aktenDaten.aktenzeichen) parts.push(`AZ: ${aktenDaten.aktenzeichen}`);
    if (aktenDaten.kennzeichen) parts.push(aktenDaten.kennzeichen);
    if (aktenDaten.schadennummer) parts.push(`SN: ${aktenDaten.schadennummer}`);
    if (aktenDaten.versicherungsnummer) parts.push(`VN: ${aktenDaten.versicherungsnummer}`);
    return parts.join(' | ');
  };

  const applyVorlage = (key: string) => {
    const v = VORLAGEN[key];
    if (!v) return;
    setDokument(prev => ({
      ...prev,
      ...v,
      betreff: (v.betreff || '')
        .replace('{schadennr}', aktenDaten.schadennummer || 'XXX')
        .replace('{aktenzeichen}', aktenDaten.aktenzeichen || 'XXX'),
      unterschrift_name: prev.unterschrift_name,
      unterschrift_titel: prev.unterschrift_titel,
      unterschrift_zusatz: prev.unterschrift_zusatz,
    }));
    setShowVorlagen(false);
    toast.success('Vorlage angewendet');
  };

  // ============================================================
  // GENERATE PRINT HTML — WinValue-Style
  // ============================================================
  const generatePrintHTML = useCallback(() => {
    const bk = briefkopf;
    const emp = empfaenger;
    const dok = dokument;
    const ad = aktenDaten;
    const refLine = buildRefLine();

    const inhaltHTML = dok.inhalt
      .split('\n\n')
      .map(p => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
      .join('');

    const anlagenHTML = dok.anlagen && dok.anlagen.length > 0 && dok.anlagen.some(a => a.trim())
      ? `<div class="anlagen-block"><strong>Anlagen</strong><br/>${dok.anlagen.filter(a => a.trim()).map(a => `&ndash; ${a}`).join('<br/>')}</div>`
      : '';

    // Footer columns for Rechnung-type docs
    const footerCol1 = `${bk.firmenname}<br/>Inhaber ${bk.inhaber}<br/>${bk.qualifikation || bk.titel}<br/>${bk.adresse}<br/>${bk.plz_ort}`;
    const footerCol2 = `<strong>Kontakt</strong><br/>Mobil: ${bk.mobil}<br/>Mail: ${bk.email}<br/>Web: ${bk.web}`;
    const footerCol3 = `${bk.bankname ? `<strong>${bk.bankname}</strong><br/>` : ''}${bk.iban ? `IBAN: ${bk.iban}` : ''}${bk.bic ? `<br/>BIC: ${bk.bic}` : ''}${bk.steuernummer ? `<br/>St.-Nr.: ${bk.steuernummer}` : ''}${bk.ust_id ? `<br/>USt-ID: ${bk.ust_id}` : ''}`;

    // Datenblock rows
    const datenLinks: [string, string][] = [];
    if (ad.aktenzeichen) datenLinks.push(['Aktenzeichen', ad.aktenzeichen]);
    if (ad.fin) datenLinks.push(['Fz.-Ident.Nr', ad.fin]);
    if (ad.schadennummer) datenLinks.push(['Schadennummer', ad.schadennummer]);
    if (ad.versicherung) datenLinks.push(['Versicherung', ad.versicherung]);

    const datenRechts: [string, string][] = [];
    if (ad.versicherungsnummer) datenRechts.push(['Vers.-Nummer', ad.versicherungsnummer]);
    if (ad.schadenort) datenRechts.push(['Schadenort', ad.schadenort]);
    if (ad.schadentag) datenRechts.push(['Schadentag', ad.schadentag]);
    if (ad.kennzeichen_gegner) datenRechts.push(['Kennz. Unfallgegner', ad.kennzeichen_gegner]);

    const hasDatenblock = datenLinks.length > 0 || datenRechts.length > 0;
    const maxRows = Math.max(datenLinks.length, datenRechts.length);

    let datenblockHTML = '';
    if (hasDatenblock) {
      let rows = '';
      for (let i = 0; i < maxRows; i++) {
        const l = datenLinks[i];
        const r = datenRechts[i];
        rows += `<tr>
          <td class="dl">${l ? l[0] : ''}</td>
          <td class="dv">${l ? l[1] : ''}</td>
          <td class="dl">${r ? r[0] : ''}</td>
          <td class="dv">${r ? r[1] : ''}</td>
        </tr>`;
      }
      datenblockHTML = `
        <div class="falldaten-bereich">
        ${ad.kennzeichen ? `<div class="kennz-zeile"><strong>Amtl. Kennzeichen: ${ad.kennzeichen}</strong></div>` : ''}
        <table class="datenblock">${rows}</table>
        </div>`;
    }

    const showFooterData = dok.typ === 'rechnung';

    return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8">
<title>${dok.betreff || 'Dokument'}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Montserrat:wght@400;500;600;700;800&display=swap');

  /* ================================================================
     WINVALUE-STYLE DOCUMENT LAYOUT
     Dual-Engine: Running Elements (WeasyPrint) + Fixed (Chrome)
     ================================================================ */

  @page {
    size: A4;
    margin: ${showFooterData ? '32mm 20mm 28mm 25mm' : '32mm 20mm 18mm 25mm'};
    @top-left {
      content: element(laufkopf);
    }
    @bottom-left {
      content: element(lauffuss);
    }
  }

  @page:first {
    margin-top: 32mm;
  }

  /* Suppress Chrome's own header/footer (date, URL, page number) */
  @media print {
    @page {
      margin: 0;
    }
  }

  /* ===== TABLE LAYOUT — spacers repeat on every page in Chrome ===== */
  .layout-table { width: 100%; border-collapse: collapse; }
  .layout-table td, .layout-table th { padding: 0; border: 0; }
  .header-spacer { height: 38mm; }
  .footer-spacer { height: 28mm; }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body {
    font-family: 'Inter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-size: 10pt;
    color: #1a1a1a;
    line-height: 1.55;
    background: white;
  }

  /* ===== RUNNING HEADER (WeasyPrint) ===== */
  .laufkopf {
    position: running(laufkopf);
    width: 165mm;
  }
  .kopf-container {
    display: flex;
    align-items: center;
    margin-bottom: 1.5mm;
    height: 16mm;
    gap: 3mm;
  }
  .kopf-logo {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: flex-start;
  }
  .kopf-logo img {
    height: 14mm;
    width: auto;
    object-fit: contain;
    display: block;
  }
  .kopf-text {
    flex: 1;
    background: #e8edf2;
    border-radius: 1.5mm;
    padding: 2mm 4mm;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  .kopf-firmenname {
    font-family: 'Montserrat', 'Inter', sans-serif;
    font-size: 11pt;
    font-weight: 700;
    color: #1a1a1a;
    letter-spacing: 0.02em;
  }
  .kopf-inhaber {
    font-family: 'Inter', sans-serif;
    font-size: 8.5pt;
    font-weight: 500;
    color: #333;
  }
  .kopf-quali {
    font-size: 7pt;
    color: #666;
    font-weight: 400;
  }
  /* Reference line below header */
  .ref-zeile {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding-top: 1.5mm;
    border-top: 0.3pt solid #ddd;
    font-size: 7pt;
    font-weight: 500;
    color: #555;
    letter-spacing: 0.01em;
  }
  .ref-links { }
  .ref-datum { }

  /* ===== RUNNING FOOTER (WeasyPrint) ===== */
  .lauffuss {
    position: running(lauffuss);
    width: 165mm;
  }
  .fuss-data {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-top: 0.3pt solid #ccc;
    padding-top: 2mm;
    font-size: 6.5pt;
    color: #888;
    line-height: 1.5;
  }
  .fuss-data .fc { flex: 1; }
  .fuss-data .fc:not(:last-child) { margin-right: 3mm; }
  .fuss-data strong { color: #666; font-weight: 600; }
  .fuss-seitenzahl {
    text-align: right;
    font-size: 7pt;
    color: #999;
    padding-top: 1mm;
  }

  /* ===== CHROME PRINT FALLBACK ===== */
  .chrome-header, .chrome-footer { display: none; }
  @media print {
    /* Hide running elements in Chrome — Chrome ignores position:running() */
    .laufkopf, .lauffuss { display: none !important; }
    .chrome-header {
      display: block;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      padding: 5mm 20mm 4mm 25mm;
      background: white;
      z-index: 9999;
    }
    .chrome-footer {
      display: block;
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 3mm 20mm 8mm 25mm;
      background: white;
      z-index: 9999;
      border-top: 0.3pt solid #ccc;
    }
  }

  .inhalt-zelle { vertical-align: top; padding: 0 22mm 0 25mm; }

  /* ===== SEITE 1: ABSENDER / EMPFÄNGER ===== */
  .absender-block {
    display: flex;
    justify-content: space-between;
    margin-bottom: 4mm;
    font-size: 8pt;
    color: #444;
    line-height: 1.5;
  }
  .absender-links {
    font-size: 8pt;
  }
  .absender-rechts {
    text-align: right;
    font-size: 8pt;
  }
  .absender-rechts .ar-firma {
    font-weight: 500;
    color: #1a1a1a;
  }
  .kontakt-block {
    margin-top: 2mm;
    font-size: 8pt;
    color: #444;
  }
  .empfaenger-block {
    font-size: 10.5pt;
    font-weight: 600;
    line-height: 1.5;
    margin-bottom: 5mm;
  }

  /* ===== BETREFF ===== */
  .betreff-zeile {
    font-family: 'Montserrat', 'Inter', sans-serif;
    font-size: 13pt;
    font-weight: 600;
    color: #1a1a1a;
    margin-bottom: 3mm;
    line-height: 1.35;
    border-bottom: 0.3pt solid #ddd;
    padding-bottom: 2mm;
  }

  /* ===== KENNZEICHEN + DATENBLOCK ===== */
  .falldaten-bereich {
    background: #f8f9fb;
    border: 0.3pt solid #e0e4e8;
    border-radius: 1mm;
    padding: 2.5mm 3mm;
    margin-bottom: 4mm;
  }
  .kennz-zeile {
    font-size: 9.5pt;
    margin-bottom: 1.5mm;
  }
  .datenblock {
    width: 100%;
    border-collapse: collapse;
    font-size: 8.5pt;
    line-height: 1.55;
  }
  .datenblock td { padding: 0.4mm 0; vertical-align: top; }
  .datenblock .dl { color: #666; width: 26mm; font-size: 8pt; }
  .datenblock .dv { color: #1a1a1a; font-weight: 500; padding-right: 6mm; }

  /* Separator line between Datenblock and Anrede */
  .daten-separator {
    border: none;
    border-top: 0.3pt solid #d0d4d8;
    margin: 3mm 0 3mm 0;
  }

  /* ===== BRIEFTEXT ===== */
  .anrede-zeile { margin-bottom: 2mm; }
  .brief-text p {
    margin: 0 0 7pt 0;
    line-height: 1.55;
    text-align: justify;
  }

  /* ===== GRUSS + UNTERSCHRIFT ===== */
  .gruss-block {
    page-break-inside: avoid;
    margin-top: 5mm;
  }
  .gruss { margin-bottom: 3mm; }
  .u-name { font-weight: 600; font-size: 10pt; }
  .u-titel { font-size: 8.5pt; color: #444; }
  .u-zusatz { font-size: 8.5pt; color: #444; font-weight: 600; }
  .u-mitglied { font-size: 8pt; color: #666; font-style: italic; }

  /* ===== ANLAGEN ===== */
  .anlagen-block {
    margin-top: 8mm;
    font-size: 9.5pt;
    page-break-inside: avoid;
  }

  @media screen {
    .chrome-header, .chrome-footer { display: none !important; }
    .header-spacer { height: 0; }
    .footer-spacer { height: 0; }
    .inhalt-zelle { max-width: 210mm; margin: 0 auto; background: white; min-height: 297mm; padding: 40mm 22mm 30mm 25mm !important; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
  }
</style>
</head>
<body>

<!-- ===== RUNNING HEADER (WeasyPrint: placed in @page @top-left) ===== -->
<div class="laufkopf">
  <div class="kopf-container">
    <div class="kopf-logo">
      <img src="${bk.logo_url}" alt="${bk.firmenname}" />
    </div>
    <div class="kopf-text">
      <div class="kopf-firmenname">${bk.firmenname}</div>
      <div class="kopf-inhaber">${bk.titel}</div>
      <div class="kopf-quali">${bk.qualifikation || ''}</div>
    </div>
  </div>
  <div class="ref-zeile">
    <div class="ref-links">${refLine}</div>
    <div class="ref-datum">${datum}</div>
  </div>
</div>

<!-- ===== RUNNING FOOTER ===== -->
<div class="lauffuss">
  ${showFooterData ? `
  <div class="fuss-data">
    <div class="fc">${footerCol1}</div>
    <div class="fc">${footerCol2}</div>
    <div class="fc">${footerCol3}</div>
  </div>` : `
  <div class="fuss-seitenzahl">
  </div>`}
</div>

<!-- ===== CHROME FIXED HEADER ===== -->
<div class="chrome-header">
  <div class="kopf-container">
    <div class="kopf-logo">
      <img src="${bk.logo_url}" alt="${bk.firmenname}" style="height:14mm;width:auto;object-fit:contain" />
    </div>
    <div class="kopf-text">
      <div class="kopf-firmenname">${bk.firmenname}</div>
      <div class="kopf-inhaber">${bk.titel}</div>
      <div class="kopf-quali">${bk.qualifikation || ''}</div>
    </div>
  </div>
  <div class="ref-zeile">
    <div class="ref-links">${refLine}</div>
    <div class="ref-datum">${datum}</div>
  </div>
</div>

<!-- ===== CHROME FIXED FOOTER ===== -->
<div class="chrome-footer">
  ${showFooterData ? `
  <div class="fuss-data">
    <div class="fc">${footerCol1}</div>
    <div class="fc">${footerCol2}</div>
    <div class="fc">${footerCol3}</div>
  </div>` : `
  <div class="fuss-seitenzahl">
  </div>`}
</div>

<!-- ===== TABLE LAYOUT: thead/tfoot spacers repeat on every page ===== -->
<table class="layout-table">
  <thead><tr><th class="header-spacer"></th></tr></thead>
  <tfoot><tr><td class="footer-spacer"></td></tr></tfoot>
  <tbody><tr><td class="inhalt-zelle">

        <!-- Absender -->
        <div class="absender-block">
          <div class="absender-links">
            ${bk.firmenname}<br/>
            ${bk.adresse}, ${bk.plz_ort}
          </div>
          <div class="absender-rechts">
            <div class="ar-firma">${bk.firmenname}</div>
            Inhaber ${bk.inhaber}<br/>
            ${bk.qualifikation || ''}<br/>
            Ausgebildet an der BVSK Akademie
            <div class="kontakt-block">
              Mobil: ${bk.mobil}<br/>
              Email: ${bk.email}
            </div>
          </div>
        </div>

        <!-- Empfänger -->
        <div class="empfaenger-block">
          ${emp.firma ? `${emp.firma}<br/>` : ''}${emp.anrede && emp.anrede !== 'Firma' ? `${emp.anrede}<br/>` : ''}${emp.name ? `${emp.name}<br/>` : ''}${emp.strasse ? `${emp.strasse}<br/>` : ''}${emp.plz_ort ? `${emp.plz_ort}<br/>` : ''}${emp.land && emp.land !== 'Deutschland' ? `${emp.land}` : ''}
        </div>

        <!-- Betreff -->
        <div class="betreff-zeile">${dok.betreff}</div>

        <!-- Datenblock -->
        ${datenblockHTML}

        <!-- Separator + Anrede + Text -->
        <hr class="daten-separator" />
        <div class="anrede-zeile">${dok.anrede}</div>
        <div class="brief-text">${inhaltHTML}</div>

        <!-- Grußformel + Unterschrift -->
        <div class="gruss-block">
          <div class="gruss">${dok.grussformel}</div>
          <div class="unterschrift">
            <div class="u-name">${dok.unterschrift_name}</div>
            <div class="u-titel">${dok.unterschrift_titel}</div>
            ${dok.unterschrift_zusatz ? `<div class="u-zusatz">${dok.unterschrift_zusatz.split('\n')[0]}</div>` : ''}
            ${dok.unterschrift_zusatz && dok.unterschrift_zusatz.split('\n')[1] ? `<div class="u-mitglied">${dok.unterschrift_zusatz.split('\n')[1]}</div>` : ''}
          </div>
        </div>

        <!-- Anlagen -->
        ${anlagenHTML}

  </td></tr></tbody>
</table>

</body>
</html>`;
  }, [briefkopf, empfaenger, dokument, aktenDaten, datum]);

  // PDF Export via print
  const handlePrint = useCallback(() => {
    const html = generatePrintHTML();
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Popup-Blocker aktiv — bitte erlauben');
      return;
    }
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  }, [generatePrintHTML]);

  // Download as HTML
  const handleDownloadHTML = useCallback(() => {
    const html = generatePrintHTML();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const filename = `${dokument.typ}_${aktenDaten.aktenzeichen || 'dokument'}_${datum.replace(/\s/g, '_')}.html`;
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Dokument heruntergeladen', { description: filename });
  }, [generatePrintHTML, dokument, aktenDaten, datum]);

  // Save callback
  const handleSave = useCallback(() => {
    const html = generatePrintHTML();
    const filename = `${dokument.typ}_${aktenDaten.aktenzeichen || 'dokument'}_${datum.replace(/\s/g, '_')}`;
    if (onSave) {
      onSave(html, filename);
    } else {
      handleDownloadHTML();
    }
  }, [generatePrintHTML, dokument, aktenDaten, datum, onSave, handleDownloadHTML]);

  // ============================================================
  // RENDER — EDITOR MODE
  // ============================================================
  if (mode === 'edit') {
    return (
      <div className="flex flex-col h-full bg-gray-50">
        {/* TOOLBAR */}
        <div className="flex items-center justify-between px-4 py-2 bg-white border-b shadow-sm">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            <span className="font-semibold text-sm">Dokument erstellen</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="relative">
              <button
                onClick={() => setShowVorlagen(!showVorlagen)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition"
              >
                <Copy className="w-3.5 h-3.5" /> Vorlage <ChevronDown className="w-3 h-3" />
              </button>
              {showVorlagen && (
                <div className="absolute right-0 top-full mt-1 w-72 bg-white rounded-lg shadow-xl border z-50 max-h-96 overflow-y-auto">
                  {/* Mietwagen-Vorlagen */}
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-orange-600 uppercase tracking-wider bg-orange-50 border-b">Mietwagen / MietGo</div>
                  <button onClick={() => applyVorlage('mw-rundschreiben-anwaelte')} className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 border-b border-gray-50">
                    <div className="font-medium">Rundschreiben Anwälte</div>
                    <div className="text-gray-500">Info über MietGo / LCS GmbH</div>
                  </button>
                  <button onClick={() => applyVorlage('mw-rundschreiben-versicherungen')} className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 border-b border-gray-50">
                    <div className="font-medium">Rundschreiben Versicherungen</div>
                    <div className="text-gray-500">Änderung Abrechnungsstelle</div>
                  </button>
                  <button onClick={() => applyVorlage('mw-forderung-versicherung')} className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 border-b border-gray-50">
                    <div className="font-medium">Forderung an Versicherung</div>
                    <div className="text-gray-500">Offene Mietwagenkosten anfragen</div>
                  </button>
                  <button onClick={() => applyVorlage('mw-mahnung-versicherung')} className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 border-b border-gray-50">
                    <div className="font-medium">Mahnung Versicherung</div>
                    <div className="text-gray-500">Zahlungserinnerung mit Frist</div>
                  </button>
                  <button onClick={() => applyVorlage('mw-mietdauer-begruendung')} className="w-full text-left px-3 py-2 text-xs hover:bg-orange-50 border-b">
                    <div className="font-medium">Mietdauer-Begründung</div>
                    <div className="text-gray-500">Verkehrssicherheit, Notreparatur, Zerlegung</div>
                  </button>
                  {/* Gutachten-Vorlagen */}
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-blue-600 uppercase tracking-wider bg-blue-50 border-b">Gutachten</div>
                  <button onClick={() => applyVorlage('anschreiben-anwalt')} className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-50">
                    <div className="font-medium">Anschreiben Anwalt</div>
                    <div className="text-gray-500">Abrechnungsschreiben anfordern</div>
                  </button>
                  <button onClick={() => applyVorlage('stellungnahme')} className="w-full text-left px-3 py-2 text-xs hover:bg-blue-50 border-b border-gray-50">
                    <div className="font-medium">Stellungnahme</div>
                    <div className="text-gray-500">Zu technischen Einwänden</div>
                  </button>
                  {/* Allgemein */}
                  <div className="px-3 py-1.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider bg-gray-50 border-b">Allgemein</div>
                  <button onClick={() => applyVorlage('freitext')} className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 rounded-b-lg">
                    <div className="font-medium">Freitext</div>
                    <div className="text-gray-500">Leeres Dokument</div>
                  </button>
                </div>
              )}
            </div>
            <button onClick={() => setMode('preview')} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition">
              <Eye className="w-3.5 h-3.5" /> Vorschau
            </button>
            <button onClick={handlePrint} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition">
              <Printer className="w-3.5 h-3.5" /> PDF
            </button>
            <button onClick={handleSave} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition">
              <Save className="w-3.5 h-3.5" /> Speichern
            </button>
            {onClose && (
              <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* EDITOR CONTENT */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-2xl mx-auto space-y-4">

            {/* KOPFZEILE VORSCHAU (gesperrt) */}
            <div className="bg-gray-100 rounded-xl border border-gray-300 p-4 opacity-80">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">Kopfzeile (gesperrt)</span>
              </div>
              <div className="flex items-center gap-3">
                <img src={briefkopf.logo_url} alt="Logo" className="h-10 object-contain" />
                <div>
                  <div className="font-bold text-sm">{briefkopf.firmenname}</div>
                  <div className="text-xs text-gray-500">{briefkopf.titel} · {briefkopf.qualifikation}</div>
                </div>
              </div>
            </div>

            {/* BETRIEB AUSWAHL */}
            <div className="bg-white rounded-xl border p-4">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Absender / Betrieb</label>
              <div className="flex flex-wrap gap-2 mt-2">
                {Object.entries(BRIEFKOPF_PRESETS).map(([key, bk]) => (
                  <button
                    key={key}
                    onClick={() => {
                      setBriefkopf(bk);
                      setDokument(prev => ({ ...prev, unterschrift_name: bk.inhaber }));
                    }}
                    className={`px-3 py-2 rounded-lg text-xs font-medium border transition ${
                      briefkopf.firmenname === bk.firmenname
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {bk.zusatz || bk.firmenname}
                  </button>
                ))}
              </div>
            </div>

            {/* EMPFÄNGER */}
            <div className="bg-white rounded-xl border p-4">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Empfänger</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div className="col-span-2">
                  <input value={empfaenger.firma || ''} onChange={e => setEmpfaenger(p => ({ ...p, firma: e.target.value }))} placeholder="Firma / Kanzlei / Versicherung" className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 focus:border-blue-400 outline-none" />
                </div>
                <select value={empfaenger.anrede || ''} onChange={e => setEmpfaenger(p => ({ ...p, anrede: e.target.value }))} className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none">
                  <option value="Firma">Firma</option>
                  <option value="Herrn">Herrn</option>
                  <option value="Frau">Frau</option>
                  <option value="">Ohne Anrede</option>
                </select>
                <input value={empfaenger.name} onChange={e => setEmpfaenger(p => ({ ...p, name: e.target.value }))} placeholder="Name" className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none" />
                <input value={empfaenger.strasse || ''} onChange={e => setEmpfaenger(p => ({ ...p, strasse: e.target.value }))} placeholder="Straße + Nr." className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none" />
                <input value={empfaenger.plz_ort || ''} onChange={e => setEmpfaenger(p => ({ ...p, plz_ort: e.target.value }))} placeholder="PLZ Ort" className="px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none" />
              </div>
            </div>

            {/* AKTENDATEN (fest, aus Akte übernommen) */}
            <div className="bg-white rounded-xl border p-4">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Aktendaten</label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Aktenzeichen</label>
                  <input value={aktenDaten.aktenzeichen || ''} onChange={e => setAktenDaten(p => ({ ...p, aktenzeichen: e.target.value }))} placeholder="AK..." className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-200 outline-none font-mono" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Kennzeichen</label>
                  <input value={aktenDaten.kennzeichen || ''} onChange={e => setAktenDaten(p => ({ ...p, kennzeichen: e.target.value }))} placeholder="XX-XX 0000" className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-200 outline-none font-mono" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">FIN / VIN</label>
                  <input value={aktenDaten.fin || ''} onChange={e => setAktenDaten(p => ({ ...p, fin: e.target.value }))} placeholder="Fahrzeug-Ident-Nr." className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-200 outline-none font-mono text-xs" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Schadennummer</label>
                  <input value={aktenDaten.schadennummer || ''} onChange={e => setAktenDaten(p => ({ ...p, schadennummer: e.target.value }))} placeholder="SN-..." className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-200 outline-none font-mono text-xs" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Versicherung</label>
                  <input value={aktenDaten.versicherung || ''} onChange={e => setAktenDaten(p => ({ ...p, versicherung: e.target.value }))} placeholder="Versicherungsname" className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-200 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Vers.-Nummer</label>
                  <input value={aktenDaten.versicherungsnummer || ''} onChange={e => setAktenDaten(p => ({ ...p, versicherungsnummer: e.target.value }))} placeholder="VN..." className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-200 outline-none font-mono text-xs" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Schadenort</label>
                  <input value={aktenDaten.schadenort || ''} onChange={e => setAktenDaten(p => ({ ...p, schadenort: e.target.value }))} placeholder="Ort" className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-200 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Schadentag</label>
                  <input value={aktenDaten.schadentag || ''} onChange={e => setAktenDaten(p => ({ ...p, schadentag: e.target.value }))} placeholder="TT.MM.JJJJ" className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-200 outline-none" />
                </div>
                <div className="col-span-2">
                  <label className="text-[10px] text-gray-400 uppercase">Kennz. Unfallgegner</label>
                  <input value={aktenDaten.kennzeichen_gegner || ''} onChange={e => setAktenDaten(p => ({ ...p, kennzeichen_gegner: e.target.value }))} placeholder="XX-XX 0000" className="w-full px-3 py-2 text-sm border rounded-lg bg-gray-50 focus:ring-2 focus:ring-blue-200 outline-none font-mono" />
                </div>
              </div>
            </div>

            {/* BETREFF + BRIEFTEXT */}
            <div className="bg-white rounded-xl border p-4">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Betreff &amp; Text</label>
              <div className="mt-2 space-y-3">
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Betreff</label>
                  <input value={dokument.betreff} onChange={e => setDokument(p => ({ ...p, betreff: e.target.value }))} placeholder="Betreff (wird groß + fett gedruckt)" className="w-full px-3 py-2 text-sm border rounded-lg font-semibold focus:ring-2 focus:ring-blue-200 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Anrede</label>
                  <input value={dokument.anrede} onChange={e => setDokument(p => ({ ...p, anrede: e.target.value }))} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none" />
                </div>
                <div>
                  <label className="text-[10px] text-gray-400 uppercase">Inhalt</label>
                  <textarea value={dokument.inhalt} onChange={e => setDokument(p => ({ ...p, inhalt: e.target.value }))} rows={14} placeholder="Brieftext hier eingeben..." className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none resize-y leading-relaxed" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-gray-400 uppercase">Grußformel</label>
                    <input value={dokument.grussformel} onChange={e => setDokument(p => ({ ...p, grussformel: e.target.value }))} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none" />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-400 uppercase">Unterschrift Name</label>
                    <input value={dokument.unterschrift_name} onChange={e => setDokument(p => ({ ...p, unterschrift_name: e.target.value }))} className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none" />
                  </div>
                </div>
              </div>
            </div>

            {/* ANLAGEN */}
            <div className="bg-white rounded-xl border p-4">
              <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Anlagen</label>
              <div className="mt-2 space-y-2">
                {(dokument.anlagen || []).map((a, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input value={a} onChange={e => { const n = [...(dokument.anlagen || [])]; n[i] = e.target.value; setDokument(p => ({ ...p, anlagen: n })); }} className="flex-1 px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-200 outline-none" />
                    <button onClick={() => { const n = (dokument.anlagen || []).filter((_, idx) => idx !== i); setDokument(p => ({ ...p, anlagen: n })); }} className="p-1 text-red-400 hover:text-red-600"><X className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => setDokument(p => ({ ...p, anlagen: [...(p.anlagen || []), ''] }))} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                  <Plus className="w-3.5 h-3.5" /> Anlage hinzufügen
                </button>
              </div>
            </div>

            {/* FUSSZEILE VORSCHAU (gesperrt) */}
            <div className="bg-gray-100 rounded-xl border border-gray-300 p-4 opacity-80">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 bg-gray-400 rounded-full" />
                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-widest">Fußzeile (gesperrt)</span>
              </div>
              <div className="flex justify-between text-[9px] text-gray-500 leading-relaxed">
                <div>{briefkopf.firmenname}<br/>{briefkopf.adresse} · {briefkopf.plz_ort}</div>
                <div>Tel: {briefkopf.telefon}<br/>{briefkopf.email}</div>
                <div>{briefkopf.bankname}<br/>{briefkopf.iban && `IBAN: ${briefkopf.iban}`}</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // RENDER — PREVIEW MODE
  // ============================================================
  const refLine = buildRefLine();
  return (
    <div className="flex flex-col h-full bg-gray-100">
      {/* TOOLBAR */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b shadow-sm">
        <div className="flex items-center gap-2">
          <Eye className="w-5 h-5 text-blue-600" />
          <span className="font-semibold text-sm">Vorschau</span>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => setMode('edit')} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition">
            <Edit3 className="w-3.5 h-3.5" /> Bearbeiten
          </button>
          <button onClick={handlePrint} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition">
            <Printer className="w-3.5 h-3.5" /> Drucken / PDF
          </button>
          <button onClick={handleDownloadHTML} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition">
            <Download className="w-3.5 h-3.5" /> HTML
          </button>
          {onClose && (
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg"><X className="w-4 h-4" /></button>
          )}
        </div>
      </div>

      {/* A4 PREVIEW */}
      <div className="flex-1 overflow-y-auto flex justify-center py-6 px-4">
        <div ref={printRef} className="bg-white shadow-2xl rounded-sm" style={{ width: '210mm', minHeight: '297mm', position: 'relative', padding: '0 22mm 0 25mm', fontFamily: "'Inter', sans-serif", fontSize: '10pt', color: '#1a1a1a', transform: 'scale(0.65)', transformOrigin: 'top center' }}>

          {/* KOPFZEILE */}
          <div style={{ display: 'flex', alignItems: 'stretch', paddingTop: '8mm' }}>
            <div style={{ width: '32mm', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src={briefkopf.logo_url} alt="Logo" style={{ height: '18mm', objectFit: 'contain' }} />
            </div>
            <div style={{ flex: 1, background: '#f0f0f0', padding: '3mm 5mm', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13pt', fontWeight: 700 }}>{briefkopf.firmenname}</div>
              <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '10pt', fontWeight: 600, color: '#333' }}>{briefkopf.titel}</div>
              <div style={{ fontSize: '8pt', color: '#666' }}>{briefkopf.qualifikation}</div>
            </div>
          </div>

          {/* REFERENZZEILE */}
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '2mm', fontSize: '8pt', fontWeight: 500 }}>
            <div>{refLine}</div>
            <div>{datum}</div>
          </div>

          {/* ABSENDER */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4mm', fontSize: '8pt', color: '#444', lineHeight: '1.5' }}>
            <div>{briefkopf.firmenname}<br/>{briefkopf.adresse}, {briefkopf.plz_ort}</div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontWeight: 500, color: '#1a1a1a' }}>{briefkopf.firmenname}</div>
              Inhaber {briefkopf.inhaber}<br/>
              {briefkopf.qualifikation}<br/>
              <div style={{ marginTop: '2mm' }}>Mobil: {briefkopf.mobil}<br/>Email: {briefkopf.email}</div>
            </div>
          </div>

          {/* EMPFÄNGER */}
          <div style={{ fontSize: '10.5pt', fontWeight: 600, lineHeight: '1.5', marginTop: '3mm', marginBottom: '5mm' }}>
            {empfaenger.firma && <>{empfaenger.firma}<br/></>}
            {empfaenger.anrede && empfaenger.anrede !== 'Firma' && <>{empfaenger.anrede}<br/></>}
            {empfaenger.name}<br/>
            {empfaenger.strasse && <>{empfaenger.strasse}<br/></>}
            {empfaenger.plz_ort || ''}
          </div>

          {/* BETREFF */}
          <div style={{ fontFamily: "'Montserrat', sans-serif", fontSize: '13pt', fontWeight: 600, marginBottom: '3mm', borderBottom: '0.3pt solid #ddd', paddingBottom: '2mm' }}>
            {dokument.betreff}
          </div>

          {/* DATENBLOCK */}
          {aktenDaten.kennzeichen && (
            <div style={{ fontSize: '9.5pt', fontWeight: 700, marginBottom: '2mm' }}>Amtl. Kennzeichen: {aktenDaten.kennzeichen}</div>
          )}

          {/* ANREDE + TEXT */}
          <div style={{ marginBottom: '2mm' }}>{dokument.anrede}</div>
          <div style={{ marginBottom: '5mm', lineHeight: '1.55', textAlign: 'justify' as const }}>
            {dokument.inhalt.split('\n\n').map((p, i) => (
              <p key={i} style={{ margin: '0 0 7pt 0' }}>{p.split('\n').map((line, j) => (
                <span key={j}>{line}{j < p.split('\n').length - 1 && <br/>}</span>
              ))}</p>
            ))}
          </div>

          {/* GRUSS + UNTERSCHRIFT */}
          <div style={{ marginTop: '5mm' }}>
            <div style={{ marginBottom: '3mm' }}>{dokument.grussformel}</div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '10pt' }}>{dokument.unterschrift_name}</div>
              <div style={{ fontSize: '8.5pt', color: '#444' }}>{dokument.unterschrift_titel}</div>
              {dokument.unterschrift_zusatz && <div style={{ fontSize: '8.5pt', color: '#444', fontWeight: 600 }}>{dokument.unterschrift_zusatz.split('\n')[0]}</div>}
              {dokument.unterschrift_zusatz?.split('\n')[1] && <div style={{ fontSize: '8pt', color: '#666', fontStyle: 'italic' }}>{dokument.unterschrift_zusatz.split('\n')[1]}</div>}
            </div>
          </div>

          {/* ANLAGEN */}
          {dokument.anlagen && dokument.anlagen.length > 0 && dokument.anlagen.some(a => a.trim()) && (
            <div style={{ marginTop: '8mm', fontSize: '9.5pt' }}>
              <strong>Anlagen</strong><br/>
              {dokument.anlagen.filter(a => a.trim()).map((a, i) => (
                <div key={i}>– {a}</div>
              ))}
            </div>
          )}

          {/* FUSSZEILE (Preview) */}
          <div style={{ position: 'absolute', bottom: '8mm', left: '25mm', right: '15mm', borderTop: '0.3pt solid #ccc', paddingTop: '2mm', display: 'flex', justifyContent: 'space-between', fontSize: '6.5pt', color: '#888', lineHeight: '1.5' }}>
            <div style={{ flex: 1, marginRight: '3mm' }}>
              {briefkopf.firmenname}<br/>
              Inhaber {briefkopf.inhaber}<br/>
              {briefkopf.adresse}<br/>
              {briefkopf.plz_ort}
            </div>
            <div style={{ flex: 1, marginRight: '3mm' }}>
              <strong style={{ color: '#666' }}>Kontakt</strong><br/>
              Mobil: {briefkopf.mobil}<br/>
              {briefkopf.email}<br/>
              {briefkopf.web}
            </div>
            <div style={{ flex: 1 }}>
              {briefkopf.bankname && <><strong style={{ color: '#666' }}>{briefkopf.bankname}</strong><br/></>}
              {briefkopf.iban && <>IBAN: {briefkopf.iban}</>}
              {briefkopf.bic && <><br/>BIC: {briefkopf.bic}</>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
